import React, { useState, useCallback, useEffect } from 'react'
import { Box, Grid } from '@mui/material'
import { fetchImports, fetchImportsStatus } from '../../../../redux/imports/importsSlice'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks'
import { setGlobalEnv } from '../../../../redux/global/globalSlice'
import { useNavigate } from 'react-router-dom'
import {
    ConnectorType,
    Env,
    EnvName,
    EnvStatus,
    EnvType,
    KaryonModules,
} from '../../../../redux/user/userTypes'
import { Historic } from '../../../../redux/imports/importsTypes'
import { Annotation, PrimaryText, SecondaryText } from '../../../../components/ui/Text'
import ImportUpload from './ImportUpload'
import { BLACK, DARK_GREY, WHITE } from '../../../../components/ui/Colors'
import { monthsAsObject } from '../../../../helpers/months'
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import copy from "copy-to-clipboard"
import StarIcon from '@mui/icons-material/Star';
import ChartBlock from '../../../../components/features/ChartBlock'
import HeatMapTable from '../../../../components/features/import/HeatMap'
import ContactDialog from './dialog/contact/ContactDialog'
import ImportHistoric from './ImportHistoric'
import { distributorImages } from '../../../../helpers/distributorsList'
import ImportDialog from '../../../../components/features/import/ImportDialog'
import AddDistributorDialog from '../../../../components/features/addDistributor/AddDistributorDialog'
import { PrimaryButton } from '../../../../components/ui/Button'

export enum ImportSatus {
    INTEGRATED = "success",
    IN_PROGRESS = "in-progress",
    MISSED = "missed"
}

export const chipConfig = {
    [ImportSatus.IN_PROGRESS]: {
        title: "En cours de validation",
        color: "#8095FF"
    },
    [ImportSatus.INTEGRATED]: {
        title: "Validé",
        color: WHITE
    }
}

interface OwnProps {
    envList: Env[]
    type: EnvType.RETAIl | EnvType.FOOD_SERVICE
    historicImports: Historic
}

export interface DialogData {
    isOpen: boolean
    envName: EnvName
    status: EnvStatus
    connectorType?: ConnectorType
}

const resetState = {
    isOpen: false,
    envName: EnvName.MYDISTRIB,
    status: EnvStatus.INACTIVATED,
}

export enum DataType {
    SELL_OUT = "sell-out",
    SELL_IN = "sell-in",
    OTHER = "other"
}

interface ImportContentProps { distrib: string, envType: EnvType, message: string, dataType: DataType }
interface UpsellTemplate { title: string, subTitle: string, subSubTitle: string, buttonText, onClick: () => void }

const UpsellTemplateBlock = ({ title, subTitle, subSubTitle, buttonText, onClick }: UpsellTemplate) => {
    return (
        <Grid container alignItems="center"
            justifyContent="center"
            width="100%" height="100%" padding="4vw">
            <Grid container flexWrap="nowrap">
                <StarIcon sx={{ color: "#FFD233", fontSize: "2vmin", mr: "0.2vw" }} />
                <PrimaryText bold color={BLACK}>
                    {title}
                </PrimaryText>
            </Grid>
            <Grid container direction="column" ml="1.3vw">
                <Grid mb="1vh">
                    <SecondaryText paddingTop="1vh" color={BLACK}>
                        {subTitle}
                    </SecondaryText>
                    <Annotation color={DARK_GREY}>
                        {subSubTitle}
                    </Annotation>
                </Grid>
                <Grid paddingTop="1vh">
                    <PrimaryButton onClick={onClick}>
                        {buttonText}
                    </PrimaryButton>
                </Grid>
            </Grid>
        </Grid >
    )
}

const ImporterContent = ({ distrib, envType, message, dataType }: ImportContentProps) => {
    return (
        <Grid >
            <PrimaryText padding="2vh" color={BLACK}>
                {message}
            </PrimaryText>
            <Grid padding="4vw">
                <ImportUpload
                    actualDistrib={distrib}
                    actualEnv={envType}
                    dataType={dataType}
                />
            </Grid>
        </Grid>
    )
}

const Import = ({ type, historicImports }: OwnProps) => {
    const navigate = useNavigate()
    const { name } = useAppSelector((state) => state.global.env)
    const [dialogData, setDialogData] = useState<DialogData>(resetState)
    const [dialogDataAdd, setDialogDataAdd] = useState<DialogData>(resetState)
    const [hasCopied, setHasCopied] = useState<boolean>(false)
    const { _id: userId, modules } = useAppSelector((state) => state.user.user)
    const { importStatus } = useAppSelector((state) => state.imports)
    const date = new Date()
    const year = date.getFullYear()
    const month = date.getMonth()
    let period = []

    const [dialogContactData, setDialogContactData] =
        useState<DialogData>(resetState)
    const dispatch = useAppDispatch()
    const _setEnv = useCallback(
        (env: Env) => dispatch(setGlobalEnv(env)),
        [dispatch]
    )
    const _getImports = useCallback(
        async (type: EnvType.FOOD_SERVICE | EnvType.RETAIl) => {
            await dispatch(fetchImports(type))
        },
        [dispatch]
    )

    for (let i = 1; i <= month; i++) {
        period.push(`${year}-${i < 10 && "0"}${i}`)
    }

    for (let i = 12; i > month; i--) {
        period.unshift(`${year - 1}-${i < 10 ? "0" : ""}${i}`)
    }

    const _getStatusImports = useCallback(
        async (type: EnvType.FOOD_SERVICE | EnvType.RETAIl) => {
            await dispatch(fetchImportsStatus(type, period))
        },
        [dispatch]
    )

    useEffect(() => {
        const getHistory = async (
            type: EnvType.FOOD_SERVICE | EnvType.RETAIl
        ) => {
            _getImports(type)
            _getStatusImports(type)
        }

        if (userId) {
            getHistory(type)
        }
    }, [_getImports, _getStatusImports, type, userId])

    const missedImports = importStatus && importStatus.map((el) => {
        return {
            distrib: el.distrib,
            statusPerMonth: Object.fromEntries(Object.entries(el.statusPerMonth).
                filter(([key, val]) => val === ImportSatus.MISSED))
        }
    });

    const listOfMissedImports = missedImports && missedImports.map((el) => Object.keys(el.statusPerMonth).map((status, index) => (
        `${el.distrib}_` + monthsAsObject[status.slice(5, 7)] + " " + status.slice(0, 4)))).flat()

    const dataTransformed = importStatus && importStatus.map((el) => {
        return {
            name: el.distrib,
            status: Object.values(el.statusPerMonth)
        }
    })

    const hasPromoModule = modules.includes(KaryonModules.PROMO)
    const hasSegmentationModule = modules.includes(KaryonModules.SEGMENTATION)

    const openModaleContact = () => {
        setDialogContactData({
            isOpen: true,
            envName: EnvName.MYDISTRIB,
            status: EnvStatus.INACTIVATED,
        })
    }

    return (
        <Grid container direction="column" wrap="nowrap" p="1vw">
            <Grid
                justifyContent="flex-start"
                mb="1vw"
            >
                <ChartBlock title="">
                    <Grid container wrap='nowrap' pt="4vh" pb="4vh">
                        <HeatMapTable data={dataTransformed} />
                        <Grid ml="10vw" maxHeight="15vh" width="15vw">
                            {listOfMissedImports.length > 0 && <Grid container wrap='nowrap' mb="2vh" justifyContent="space-between">
                                <PrimaryText bold color={BLACK}>Fichiers à importer</PrimaryText>
                                {hasCopied ? <CheckIcon
                                    onClick={() => {
                                        copy(listOfMissedImports.join("\n"))
                                    }}
                                    sx={{ mr: 2, cursor: "pointer" }}
                                /> : <ContentCopyIcon
                                    onClick={() => {
                                        setHasCopied(true)
                                        copy(listOfMissedImports.join("\n"))
                                    }}
                                    sx={{ mr: 2, cursor: "pointer" }}
                                />}
                            </Grid>}
                            {listOfMissedImports && <Grid overflow="auto" maxHeight="80%">
                                {listOfMissedImports.map((el) =>
                                    <PrimaryText color={BLACK}>
                                        {el}
                                    </PrimaryText>
                                )}
                            </Grid>}
                        </Grid>
                    </Grid>
                </ChartBlock>
            </Grid >
            <Grid
                container
                wrap="nowrap"
                justifyContent="flex-start"
                minHeight="55%"
                mb="1vw"
            >
                <Grid item width="100%" height="100%">
                    <ChartBlock title="Données sell-out">
                        <ImporterContent dataType={DataType.SELL_OUT} distrib={name} envType={type}
                            message="Déposez ici vos fichiers sell-out bruts mensuels et trimestriels." />
                    </ChartBlock>

                </Grid>
            </Grid>
            <Grid
                container
                gap={3}
                wrap="nowrap"
                justifyContent="flex-start"
                mb="1vw"
                width="100%"
            >
                <Grid flex={2}>
                    <ChartBlock title='Catégories, segmentations, nomenclatures'>
                        {hasSegmentationModule ?
                            (<ImporterContent dataType={DataType.OTHER} distrib={name} envType={type}
                                message="Déposez ici vos fichiers comprenant vos catégorisations produit et vos segmentations régionales." />) :
                            (
                                <UpsellTemplateBlock title='Intégrez vos nomenclature : catégories produit et régions commerciales'
                                    subTitle='Allez plus loin dans la personnalisation et le suivi de vos équipes.'
                                    subSubTitle='Pour en savoir plus, rapprochez-vous de l’équipe !' buttonText='Contacter l’équipe commerciale'
                                    onClick={() =>
                                        openModaleContact()
                                    } />
                            )
                        }
                    </ChartBlock>
                </Grid>
                <Grid flex={2}>
                    <ChartBlock title='Module promo : Données sell-in'>
                        {hasPromoModule ?
                            (<ImporterContent dataType={DataType.SELL_IN} distrib={name} envType={type}
                                message="Déposez ici vos fichiers de données sell-in (données internes de facturation)." />) :
                            (
                                <UpsellTemplateBlock title='Gardez l’oeil sur la performance de vos plans promotionnels'
                                    subTitle='Ayez un temps d’avance, activez vos leviers de performance promo.'
                                    subSubTitle='Plus d’info ci-dessous !' buttonText='Découvrir le module'
                                    onClick={() => {
                                        navigate(`/food-service/${name}/promoDemo`)
                                    }
                                    } />
                            )
                        }
                    </ChartBlock>
                </Grid>
            </Grid>
            {/* HIDE HISTORIC SINCE CLEANING DONE */}
            {/* <Grid item height="45vh">
                <ImportHistoric list={historicImports.list} />
            </Grid> */}
            <ContactDialog
                isOpen={dialogContactData.isOpen}
                handleClose={() => {
                    setDialogContactData(resetState)
                }}
            />
            <ImportDialog
                img={distributorImages[dialogData.envName]}
                dialog={dialogData}
                type={type}
                connectorType={dialogData.connectorType}
                handleClose={() => {
                    setDialogData({
                        ...resetState,
                        connectorType: dialogData.connectorType,
                    })
                }}
            />
            <AddDistributorDialog
                type={type}
                handleClose={() => {
                    setDialogDataAdd(resetState)
                }}
                isOpen={dialogDataAdd.isOpen}
            />
        </Grid >
    )
}
export default Import
